import React, { useMemo, useState, useEffect, useContext } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import DataTable from "../common/DataTable";
import { AuthContext } from "../../context/AuthContext";
import CustomLoader from "../common/CustomLoader";
import { genericGet } from "../../services/apiServices";
import { GET_EXPENSE_PARTY_LEDGER_REPORT, } from "../../services/apiRoutes";
import CustomReport from "../common/CustomReport";
import { useCompanyDateFormat } from "../../constants/useCompanyDateFormat";
import { Box, Grid, IconButton } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import CustomFilter from "../common/CustomFilter";
import CustomAutocomplete from "../common/CustomAutoComplete";
import CustomButton from "../common/CustomButton";
import { getPartyLedger } from "../../services/CommonServices";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import { genericPost } from "../../services/authServices";
import CustomDataTable from "../common/CustomDataTable";
import { AccessDenied, hasCreatePermission, hasDeletePermission, hasEditPermission, hasViewPermission } from "../../services/CommonServices";
import { useLocation } from "react-router-dom";

const title = "Expense Party Ledger Report";

const ExpensePartyLedgerReport = () => {
    const { t, i18n } = useTranslation();
    const companyDateFormat = useCompanyDateFormat();
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [permission, setPermissions] = useState()
    const { token } = useContext(AuthContext)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [partyLedger, setPartyLedger] = useState([]);
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const location = useLocation();
    const currentPath = location.pathname;
    useEffect(() => {
        let rolePermisionsData = JSON.parse(sessionStorage.getItem('userData')) || []
        let editPermision = hasEditPermission(currentPath, rolePermisionsData?.permissions)
        let deletePermission = hasDeletePermission(currentPath, rolePermisionsData?.permissions)
        let createPermision = hasCreatePermission(currentPath, rolePermisionsData?.permissions)
        let viewPermision = hasViewPermission(currentPath, rolePermisionsData?.permissions)

        setPermissions({
            edit: editPermision,
            delete: deletePermission,
            create: createPermision,
            view: viewPermision,
        })
    }, [])

    const [filterPayload, setFilterPayload] = useState({
        partyLedgerId: '',
    });
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                partyLedgerId: '',
            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);
    useEffect(() => {
        getCommonData();
    }, []);
    const getCommonData = async () => {
        const partyLedger = await getPartyLedger();
        setPartyLedger(partyLedger);
    }

    const columns = useMemo(
        () => [
            { accessorKey: "index", header: "SN.", size: 50, tableHeader: { alignItems: 'center', }, tableStyle: { textAlign: "center" }, enableColumnFilter: false },

            {
                accessorKey: "partyName", header: "Party Name", size: 200, tableHeader: {
                    justifyContent: 'flex-start',
                },
            },
            {
                accessorKey: "currencySymbol",
                header: "Currency",
                size: 100,
                tableStyle: { textAlign: "center" },
            },
            {
                accessorKey: "totalBillingAmount",
                header: "Total Billing Amount",
                size: 180,
                tableStyle: { textAlign: "center" },
            },
            {
                accessorKey: "totalPaymentAmount",
                header: "Total Payment Amount",
                size: 180,
                tableStyle: { textAlign: "center" },
            },
            {
                accessorKey: "totalBalanceAmount",
                header: "Balance Amount",
                size: 180,
                tableStyle: { textAlign: "center" },
            },
            {
                accessorKey: "action",
                header: "Action",
                size: 50,
                Cell: ({ row }) => (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1px' }}>
                        <IconButton onClick={() => handleViewClick(row.original.partyLedgerId)}>
                            <VisibilityIcon />
                        </IconButton>
                    </div>
                ),
            },
        ],
        [permission]
    );


    const fetchData = async (pageIndex = 0, pageSize = 10) => {
        setLoading(true);
        setError(null);

        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };

            const response = await genericPost(GET_EXPENSE_PARTY_LEDGER_REPORT, payload, {});

            if (response?.data?.success && Array.isArray(response?.data?.data)) {

                const ledgerList = response.data.data.map((item, index) => ({
                    partyLedgerId: item?.partyLedgerId || "",
                    index: (index + 1).toString().padStart(2, "0"),
                    partyName: item?.partyName || "-",

                    currencySymbol: item?.currency_name || "-",  // **column: currencySymbol**

                    totalBillingAmount: `${item?.currency_symbol || ""} ${(Number(item?.totalExpenseAmountInBaseCurrency) || 0).toFixed(2)}`,
                    totalPaymentAmount: `${item?.currency_symbol || ""} ${(Number(item?.totalPaymentAmountInBaseCurrency) || 0).toFixed(2)}`,
                    totalBalanceAmount: `${item?.currency_symbol || ""} ${(Number(item?.totalBalanceAmountInBaseCurrency) || 0).toFixed(2)}`
                }));

                setData(ledgerList);

                setRowCount(response.data.pagination?.totalRecords || 0);

            } else {
                setError("No data found for Expense Party Ledger Summary Report.");
            }
        } catch (err) {
            console.error("Error fetching Expense Party Ledger:", err);
            setError("Failed to fetch Expense Party Ledger Summary Report.");
        } finally {
            setLoading(false);
        }
    };



    useEffect(() => {
        fetchData(page, pageSize)
    }, [token, page, pageSize])
    const handleViewClick = (partyLedgerId) => {
        navigate(`/app/reports/expensePartyLedgerReport/${partyLedgerId}`);
    };

    const defaultView = 'expensePartyLedgerReport';
    const mainContent = (
        <>
            <CustomLoader open={loading} />
            <CustomDataTable
                title={title}
                columns={columns}
                data={data}
                handleFilterButton={filterOpen}
                fiterButtonTitile="Fiter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
            />

            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomAutocomplete
                                        options={partyLedger}
                                        getOptionLabel={(option) => option.title}
                                        placeholder="Select Party Ledger"
                                        value={partyLedger.find((item) => item.id === filterPayload.partyLedgerId) || null}
                                        onChange={(event, newValue) => handleFilterChange("partyLedgerId", newValue?.id)}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );


    const getMainContent = (mainContent, permission) => {
        return permission?.view ? mainContent : null;
    };
    return (
        <>
            <CustomReport
                defaultView={defaultView}
                mainContent={getMainContent(mainContent, permission)} />
            {!permission?.view && <AccessDenied />}
        </>
    );
};
export default ExpensePartyLedgerReport;


